import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Flex, InputNumber, message, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PartnerRewardConfigItem } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { FEATURE } from 'constants/feature'
import {
  getPartnerRewardTokenLabel,
  PARTNER_REWARD_TOKEN_OPTIONS,
} from 'constants/partner-referral'
import { PTOTypeEnum } from 'constants/pto'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { logError, logInfo } from 'utils/logger'

const getReward = (record: PartnerRewardConfigItem) => record.reward ?? 0

// The partner API returns ptoType as a number (2 = Rental); legacy shape used a string enum.
const renderPtoTypeTag = (type: number | string) => {
  const isRental = type === 2 || type === PTOTypeEnum.RENTAL_PTO
  return isRental ? <Tag color="purple">Rental</Tag> : <Tag color="blue">PTO</Tag>
}

const fetchPartnerReferralConfigs = async (searchParams: Record<string, any>) => {
  const response = await AppAdmin.getPartnerReferralConfigs(searchParams)
  const payload = response.data?.data
  logInfo('Partner referral configs', payload)

  if (payload) {
    return {
      configs: (payload.results ?? []) as PartnerRewardConfigItem[],
      total: payload.pagination?.totalResults ?? payload.totalResults ?? 0,
    }
  }
  return { configs: [], total: 0 }
}

export const PartnerPtosTable = () => {
  const queryClient = useQueryClient()
  const { searchParams, initPagination, onChangePagination } = useSearchParams()
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO_REFERRAL)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState<number>(0)
  const [editingToken, setEditingToken] = useState<number>(1)
  const [saving, setSaving] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['partnerReferralConfigs', searchParams],
    queryFn: () => fetchPartnerReferralConfigs(searchParams),
    gcTime: 30000,
  })

  const configs = data?.configs ?? []
  const total = data?.total ?? 0

  const handleEdit = (record: PartnerRewardConfigItem) => {
    setEditingId(record.ptoId)
    setEditingValue(getReward(record))
    setEditingToken(record.rewardToken ?? 1)
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditingValue(0)
    setEditingToken(1)
  }

  const handleSave = async (record: PartnerRewardConfigItem) => {
    if (editingValue < 0) {
      message.error('Referral reward must be a positive number')
      return
    }

    try {
      setSaving(true)

      await AppAdmin.updatePartnerRewardConfig(record.ptoId, {
        reward: editingValue,
        rewardToken: editingToken,
      })

      message.success('Partner referral reward updated successfully')
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: ['partnerReferralConfigs'] })
    } catch (error) {
      logError('Error updating partner referral reward:', error)
      message.error('Failed to update referral reward')
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<PartnerRewardConfigItem> = [
    {
      title: 'PTO Name',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 300,
    },
    {
      title: 'PTO Type',
      dataIndex: 'ptoType',
      key: 'ptoType',
      width: 120,
      render: (type: number | string) => renderPtoTypeTag(type),
    },
    {
      title: 'PTO Price',
      dataIndex: 'pricePerNFT',
      key: 'pricePerNFT',
      width: 150,
      render: (price: number) => `$${new Intl.NumberFormat('en-US').format(price ?? 0)}`,
    },
    {
      title: 'Referral Reward',
      dataIndex: 'reward',
      key: 'reward',
      width: 280,
      render: (_: number, record: PartnerRewardConfigItem) => {
        const isEditing = editingId === record.ptoId

        if (isEditing) {
          return (
            <Flex align="center" gap={8}>
              <InputNumber
                value={editingValue}
                onChange={(val) => setEditingValue(val ?? 0)}
                min={0}
                precision={2}
                controls={false}
                autoFocus
                style={{ width: 120 }}
                onPressEnter={() => handleSave(record)}
              />
              <Select
                value={editingToken}
                onChange={setEditingToken}
                options={PARTNER_REWARD_TOKEN_OPTIONS}
                style={{ width: 100 }}
              />
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleSave(record)}
                loading={saving}
                style={{ color: '#52c41a' }}
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={saving}
                style={{ color: '#ff4d4f' }}
              />
            </Flex>
          )
        }

        const tokenLabel = getPartnerRewardTokenLabel(record.rewardToken)
        return (
          <Space>
            <span>
              {new Intl.NumberFormat('en-US').format(getReward(record))} {tokenLabel}
            </span>
            {canEdit && (
              <Button
                type="text"
                icon={<EditOutlined style={{ color: '#1677ff' }} />}
                onClick={() => handleEdit(record)}
                size="small"
              />
            )}
          </Space>
        )
      },
    },
    {
      title: 'Total Payout',
      dataIndex: 'totalPayout',
      key: 'totalPayout',
      width: 200,
      render: (payout: { amount?: number }[], record: PartnerRewardConfigItem) => {
        const label = getPartnerRewardTokenLabel(record.rewardToken)
        if (!payout?.length) return `0 ${label}`
        const sum = payout.reduce((acc, item) => acc + (item.amount ?? 0), 0)
        return `${new Intl.NumberFormat('en-US').format(sum)} ${label}`
      },
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={configs}
      rowKey="ptoId"
      loading={isLoading}
      pagination={initPagination(total)}
      onChange={onChangePagination}
      scroll={{ x: 1000 }}
      bordered
    />
  )
}
